import { useEffect, useRef, useState } from "react";

// prime react component
import AppBreadcrumb from "../../components/CustomeBreadcrumb/AppBreadcrumb";
import { Toolbar } from "primereact/toolbar";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Toast } from "primereact/toast";
import { Avatar } from "primereact/avatar";
import { InputNumber } from "primereact/inputnumber";

// data table option
import {
  defaultPageRowOptions,
  defaultRowOptions,
  paginatorLinks,
  showingEntries,
  SkeletonbodyTemplate,
  Skeletonitems,
} from "../../appconfig/Settings";

// Annuounce List Columns
import { AnnuounceListColumns } from "../../appconfig/DataTableColumns";

// api services
import { getAllDetails, postAllData } from "../../service/ProductService";

// api constant
import {
  GET_ANNOUCE_LIST,
  GET_CROP_LIST,
  SEND_ANNOUNCEMENT,
} from "../../config/ApiConstant";
import { NavLink } from "react-router-dom";
import { FilterMatchMode } from "primereact/api";
import { globalNameFilterTemplate } from "../../appconfig/AppHelper";
import { globalFieldTemplate } from "../../appconfig/TemplateSetting";
import { MultiSelect } from "primereact/multiselect";
import { ErrorMessage, Form, Formik } from "formik";
import { announceValidation } from "../../config/Validate";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";

const announceInitialValues = {
  title: "",
  message: "",
};

const AnnounceList = () => {
  document.title = "Announcements | Agriown";

  // Set Toast
  const toast = useRef<Toast>(null);
  const formikRef = useRef<any>(null);

  // page load | filter properties
  const [pageLoad, setPageLoad] = useState<boolean>(false);
  const [globalFilter, setGlobalFilter] = useState<any>(null);

  // datatable filter value sets
  const emptyFilters = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    email: { value: null, matchMode: FilterMatchMode.CONTAINS },
    mobile: { value: null, matchMode: FilterMatchMode.CONTAINS },
    status: { value: null, matchMode: FilterMatchMode.EQUALS },
  };
  const [filters, setFilters] = useState<any>(emptyFilters);

  // state to store announcement list
  const [announceList, setAnnounceList] = useState<any>([]);
  const [announceDialog, setAnnounceDialog] = useState(false);

  const [selectedAnnouncement, setSelectedAnnouncement] = useState<any>(null);

  const [cropList, setCropList] = useState<any[]>([]);
  const [selectedCrops, setSelectedCrops] = useState<any[]>([]);
  const [areaMin, setAreaMin] = useState<any>(null);
  const [areaMax, setAreaMax] = useState<any>(null);

  // useefect to get announcement list on page load
  useEffect(() => {
    getCropList();
    getAnnouceList(true);
  }, []);

  //   get crop list
  const getCropList = async () => {
    try {
      const response = await getAllDetails(GET_CROP_LIST);
      setCropList(response || []);
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message,
      });
    }
  };

  // Handle submit
  const submitAnnouncement = async (values: any, { resetForm }: any) => {
    try {
      const farmerIds = selectedAnnouncement.map((data: any) => data.id);

      const payload = {
        title: values.title,
        message: values.message,
        farmer_ids: farmerIds,
      };

      const res = await postAllData(SEND_ANNOUNCEMENT, payload);

      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: res?.message,
      });

      resetForm();
      setAnnounceDialog(false);
      setSelectedAnnouncement(null);
      getAnnouceList();
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message,
      });
    }
  };

  // left toolbar
  const leftToolbarTemplate = () => {
    return (
      <div className="grid left-toolbar-template align-items-center">
        <div className="col-3">
          <span className="search-bar">
            <i className="pi pi-search"></i>
            <InputText
              type="search"
              className="search-input"
              value={globalFilter}
              onInput={(e) => setGlobalFilter(e.currentTarget.value)}
              placeholder="Search..."
            />
          </span>
        </div>
        <div className="col-3">
          {/* Crop MultiSelect */}
          <MultiSelect
            value={selectedCrops}
            options={cropList}
            onChange={(e) => setSelectedCrops(e.value)}
            optionLabel="crop_name"
            optionValue="id"
            placeholder="Select Crops"
            display="chip"
          />
        </div>
        <div className="col-2">
          {/* Area Min */}
          <InputNumber
            value={areaMin}
            onValueChange={(e) => setAreaMin(e.value)}
            placeholder="Area Min"
            className="w-full"
          />
        </div>
        <div className="col-2">
          {/* Area Max */}
          <InputNumber
            value={areaMax}
            onValueChange={(e) => setAreaMax(e.value)}
            placeholder="Area Max"
            className="w-full"
          />
        </div>
        <div className="col-1">
          <Button
            icon="pi pi-search"
            className="p-button p-button-outlined"
            tooltip="search"
            onClick={() => getAnnouceList()}
            tooltipOptions={{ position: "top" }}
          />
        </div>
      </div>
    );
  };

  //Clear Filters
  const rightToolbarTemplate = () => {
    return (
      <>
        {!window.cn(selectedAnnouncement) && (
          <Button
            icon="pi pi-microphone"
            label="Announcement"
            onClick={() => setAnnounceDialog(true)}
          />
        )}
        <Button
          icon="pi pi-filter-slash"
          label="Clear"
          onClick={() => clearAll()}
          className="md:ml-2 p-button-outlined p-button-primary"
        ></Button>
      </>
    );
  };

  //Clear All Filter
  const clearAll = () => {
    setFilters({ ...emptyFilters });
    setGlobalFilter("");
    setSelectedCrops([]);
    setAreaMin(null);
    setAreaMax(null);
    getAnnouceList(true);
  };

  //name Template
  const nameTemplate = (rowData: any) => {
    return (
      <>
        <NavLink to={`/farmer/detail/${btoa(rowData?.id)}`}>
          <div className="flex avatar-div align-items-center">
            {!window.cn(rowData?.profile_img) ? (
              <Avatar image={rowData?.profile_img} shape="circle" />
            ) : (
              <div className="img-fluid d-flex align-items-center">
                <Avatar
                  label={rowData?.name?.charAt(0).toUpperCase()}
                  style={{
                    backgroundColor: "#438440",
                    color: "#ffffff",
                  }}
                  shape="circle"
                />
              </div>
            )}
            <div className="ml-2 emp-name link-name">{rowData.name}</div>
          </div>
        </NavLink>
      </>
    );
  };

  // total area Template
  const totalAreaTemplate = (rowData: any) => {
    let total = 0;
    if (rowData.farms && Array.isArray(rowData.farms)) {
      total = rowData.farms.reduce((acc: number, farm: any) => {
        if (!farm.is_deleted) {
          return acc + (parseFloat(farm.land_area) || 0);
        }
        return acc;
      }, 0);
    }
    return <span>{total.toFixed(2)}</span>;
  };

  //get All AnnounceList
  const getAnnouceList = async (isClear = false) => {
    try {
      const params = new URLSearchParams() as any;

      if (!isClear) {
        if (!window.cn(selectedCrops) && selectedCrops.length > 0)
          params.append("crops", selectedCrops.join(","));

        if (!window.cn(areaMin)) params.append("area_min", areaMin);
        if (!window.cn(areaMax)) params.append("area_max", areaMax);
      }

      const queryString = params.toString();

      const response = await getAllDetails(
        queryString ? `${GET_ANNOUCE_LIST}?${queryString}` : GET_ANNOUCE_LIST
      );

      setAnnounceList(response || []);
      setPageLoad(true);
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message,
      });
      setPageLoad(false);
    }
  };

  return (
    <>
      {/* Breadcrumb */}
      <AppBreadcrumb
        currentMenu="Announcements"
        pageName="Announcements List"
        pageTitle="Announcements List"
      />

      <Toast ref={toast} />

      <div className="grid crud-demo">
        <div className="col-12">
          <div className="card">
            <Toolbar
              left={leftToolbarTemplate}
              right={rightToolbarTemplate}
            ></Toolbar>

            {pageLoad ? (
              <DataTable
                value={announceList}
                showGridlines
                stripedRows
                globalFilter={globalFilter || null}
                rows={defaultRowOptions}
                paginator={announceList.length >= 10}
                rowsPerPageOptions={defaultPageRowOptions}
                className="datatable-responsive p-datatable-sm"
                paginatorTemplate={paginatorLinks}
                currentPageReportTemplate={showingEntries}
                filters={{ ...filters, global: { ...filters.global, value: globalFilter || null } }}
                onFilter={(e: any) => setFilters(e.filters)}
                emptyMessage="No Data Found"
                removableSort
                selection={selectedAnnouncement}
                onSelectionChange={(e) => setSelectedAnnouncement(e.value)}
              >
                <Column
                  selectionMode="multiple"
                  headerStyle={{ width: "3rem" }}
                ></Column>
                {AnnuounceListColumns.map((col, i) => {
                  if (col.field === "name") {
                    return (
                      <Column
                        key={col.field}
                        field={col.field}
                        header={col.header}
                        body={nameTemplate}
                        sortable
                        filter
                        filterElement={globalNameFilterTemplate}
                      />
                    );
                  } else if (col.field === "email") {
                    return (
                      <Column
                        key={col.field}
                        header={col.header}
                        body={globalFieldTemplate}
                        field={col.field}
                      />
                    );
                  } else if (col.field === "mobile") {
                    return (
                      <Column
                        key={col.field}
                        header={col.header}
                        body={globalFieldTemplate}
                        field={col.field}
                      />
                    );
                  } else if (col.field === "total_area") {
                    return (
                      <Column
                        key={col.field}
                        header={col.header}
                        body={totalAreaTemplate}
                        field={col.field}
                      />
                    );
                  } else {
                    return (
                      <Column
                        key={col.field}
                        field={col.field}
                        header={col.header}
                      />
                    );
                  }
                })}
              </DataTable>
            ) : (
              <DataTable value={Skeletonitems}>
                {AnnuounceListColumns.map((col) => (
                  <Column
                    key={col.field}
                    field={col.field}
                    header={col.header}
                    body={SkeletonbodyTemplate}
                  />
                ))}
              </DataTable>
            )}
          </div>
        </div>
      </div>

      <Dialog
        visible={announceDialog}
        header="Send Announcement"
        style={{ width: "500px" }}
        modal
        draggable={false}
        onHide={() => setAnnounceDialog(false)}
        footer={
          <div>
            <Button
              label="Cancel"
              className="p-button-secondary"
              icon="pi pi-times"
              onClick={() => setAnnounceDialog(false)}
            />
            <Button
              label="Send"
              icon="pi pi-check"
              onClick={() => formikRef.current?.handleSubmit()}
            />
          </div>
        }
      >
        <Formik
          innerRef={formikRef}
          initialValues={announceInitialValues}
          validationSchema={announceValidation}
          onSubmit={submitAnnouncement}
        >
          {({ values, errors, touched, handleChange, handleBlur }) => (
            <Form className="p-fluid p-formgrid grid">
              {/* Title */}
              <div className="field col-12">
                <label htmlFor="title">Title *</label>
                <InputText
                  id="title"
                  name="title"
                  value={values.title}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={touched.title && errors.title ? "p-invalid" : ""}
                />
                <small className="p-error">
                  <ErrorMessage name="title" />
                </small>
              </div>

              {/* Message */}
              <div className="field col-12">
                <label htmlFor="message">Message *</label>
                <InputTextarea
                  rows={3}
                  id="message"
                  name="message"
                  autoComplete="off"
                  value={values.message}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={
                    touched.message && errors.message ? "p-invalid" : ""
                  }
                />

                <small className="p-error">
                  <ErrorMessage name="message" />
                </small>
              </div>
            </Form>
          )}
        </Formik>
      </Dialog>
    </>
  );
};

export default AnnounceList;
